import { useTranslation } from "react-i18next";
import { PageDataStats } from "@/components/PageDataStats";
import { UNBOUND_STAT_TILE, type UseTableParams } from "@/hooks/useTableParams";
import { useCompanyGetEventStatsQuery } from "@/services/(company)/api.events";
import { EVENT_STATUSES } from "@/types/(company)/events.interface";
import type { EventFilters } from "./events.params";

interface Props {
  /**
   * The events table's URL params. Passing them makes each tile a status
   * filter; EventsSharedLayout renders this same row above an event *detail*
   * page, where there is no list to filter, so it leaves this out.
   */
  params?: UseTableParams<EventFilters>;
}

export const EventStats = ({ params }: Props) => {
  const { t } = useTranslation("events");
  const { data: stats, isLoading } = useCompanyGetEventStatsQuery();
  // Each tile is the status filter it counts; the total is "no filter".
  const on = (status: string) =>
    params ? params.statFilter({ status }) : UNBOUND_STAT_TILE;

  const statsData = [
    {
      name: t("events:company.stats.total_events"),
      value: stats?.data.totalEvents ?? 0,
      icon: "/icons/interface-icons/stats/events/icon-3d-calendar.png",
      ...on(""),
    },
    {
      name: t("events:company.stats.completed_events"),
      value: stats?.data.completedEvents ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-green-check.png",
      ...on(EVENT_STATUSES.COMPLETED),
    },
    {
      name: t("events:company.stats.cancelled_events"),
      value: stats?.data.cancelledEvents ?? 0,
      icon: "/icons/interface-icons/stats/shared/icon-3d-red-x.png",
      ...on(EVENT_STATUSES.CANCELLED),
    },
    {
      name: t("events:company.stats.archived_events"),
      value: stats?.data.archivedEvents ?? 0,
      icon: "/icons/interface-icons/stats/events/icon-3d-drawer.png",
      ...on(EVENT_STATUSES.ARCHIVED),
    },
  ];

  return <PageDataStats data={statsData} loading={isLoading} />;
};
